import React, { useContext, useState, useEffect } from 'react'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { useTranslation, Trans } from 'gatsby-plugin-react-i18next'

import Spinner from 'src/components/Spinner'
import LoadingEllipsis from 'src/components/LoadingEllipsis'
import StoreContext from 'src/context/StoreContext'
import theme from 'src/utils/styles/theme'
import Analytics from 'src/utils/Analytics'
import UTMParams from 'src/utils/UTMParams'
import LocalStorage from 'src/utils/LocalStorage'
import { CenteredContent } from 'src/utils/styles/global-styles'
import loadingGif from 'src/gifs/loading.gif'
import { FormattedCurrency } from 'src/utils/FormattedCurrency'
import ShopPay from 'src/components/temp/ShopPay'
import ToolTip from 'src/components/temp/ToolTip'
import BetterHelpOffer from 'src/components/temp/BetterHelpOffer'
import {
  currencyCode,
  IN_AU,
  IN_UK,
  RETURN_GUARANTEE_PRODUCT_HANDLE,
  LOVEVERY_PRODUCT_HANDLE,
  IS_FOREIGN_COUNTRY,
  SHOW_BETTER_HELP_DESIGN,
} from 'src/utils/constants'
import ProductsList from '../ProductsList'
import LineItem from './LineItem'
import Popup from '../Popup'
import {
  Table,
  TableHeader,
  DesktopOnlyTableHeader,
  Checkout,
  LineItemTotal,
  CheckoutButton,
  Excluding,
  PayPalCheckoutButton,
  CheckoutButtons,
  Discount,
  Total,
  QuantityHeader,
  TableRow,
  TD,
  DisplayOnDesktop,
  DisplayOnMobile,
  Amount,
  Container,
  HiddenOnDesktop,
  CheckoutContainer,
  Header,
  ProductListContainer,
  DiscountText,
  Subtotal,
  Error,
  ALink,
  Offer,
} from './styles'
import { shouldRemoveReturnGuarantee } from '../../utils/addReturnGuarantee'
import { shouldRemoveBetterHelpProduct } from '../../utils/addBetterHelp'

const Cart = () => {
  const { t } = useTranslation('common')

  const {
    store: { cart, loading },
    applyDiscount,
    isUserBuyingPlus,
    isUserBuyingCalendar,
    removeLineItem,
  } = useContext(StoreContext)
  const { paypalLogo, snapFrame } = useStaticQuery(graphql`
    query CartQuery {
      paypalLogo: file(relativePath: { eq: "paypal-logo.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, width: 75, height: 21, layout: FIXED, placeholder: NONE)
        }
      }
      snapFrame: shopifyProduct(handle: { eq: "snap-frame" }) {
        id
        title
        handle
        shopifyId
        variants {
          id
          title
          price
          shopifyId
          sku
        }
      }
    }
  `)

  const [updatingPrices, setUpdatingPrices] = useState(false)
  const [gettingCheckout, setGettingCheckout] = useState(false)
  const [displayPopup, setDisplayPopup] = useState(false)
  const [hasFrame2, setHasFrame2] = useState(false)
  const [hasSnapFrame, setHasSnapFrame] = useState(false)
  const [hasBetterHelp, setHasBetterHelp] = useState(false)
  const [hasCalendar, setHasCalendar] = useState(false)

  const [checkShowPopup, setcheckShowPopup] = useState(false)
  const [displayToolTip, setDisplayToolTip] = useState(false)

  /**
   * Nested array/object destructuring with default values:
   * Basically for each level of nesting, we are giving the array/object we are
   * trying to destructure out of a default value of empty array/object to avoid null errors .
   */
  const {
    discountAllocations = [],
    cost: { subtotalAmount: { amount: subtotalAmount } = {} } = {},
    totalQuantity,
    lines: { edges: lineItems = [] } = {},
  } = cart

  const buyingPlus = isUserBuyingPlus(cart)
  const buyingCalendar = isUserBuyingCalendar(cart)

  const discountAmountString = lineItems
    .reduce(
      (total, lineItem) =>
        total +
        lineItem?.node?.discountAllocations?.reduce(
          (lineTotal, allocation) => lineTotal + parseFloat(allocation.discountedAmount.amount),
          0
        ),
      0
    )
    .toFixed(2)
  let discountAmount = parseFloat(discountAmountString) || 0
  const cartLevelDiscount = discountAllocations.length > 0

  const subtotalNumber = parseFloat(subtotalAmount)
  let displayedSubtotal = subtotalNumber + discountAmount
  let displayedTotal = subtotalNumber

  if (discountAmount === 0 && cartLevelDiscount) {
    discountAmount = discountAllocations.reduce(
      (total, allocation) => total + parseFloat(allocation.discountedAmount.amount),
      0
    )
    displayedSubtotal = subtotalNumber
    displayedTotal = subtotalNumber - discountAmount
  }
  const TRACKER_PARAM_TIMEOUT_MILLI = 1000

  const safeTrackerParam = (cb) => {
    let finished = false
    setTimeout(() => {
      if (!finished) cb()
    }, TRACKER_PARAM_TIMEOUT_MILLI)
    // eslint-disable-next-line no-undef
    ga((tracker) => {
      const param = tracker.get('linkerParam')
      finished = true
      cb(param)
    })
  }

  const handleCheckout = async () => {
    setGettingCheckout(true)
    Analytics.track('Initiated Checkout')
    window.dataLayer = window.dataLayer || []
    const products = cart.lines.edges.map(({ node }) => ({
      id: node.merchandise.id,
      name: node.merchandise.product.title,
      price: parseFloat(node.merchandise.priceV2.amount),
      quantity: node.quantity,
    }))
    window.dataLayer.push({
      event: 'beginCheckout',
      ecommerce: {
        currencyCode,
        beginCheckout: products,
        value: parseFloat(displayedTotal),
      },
    })

    let queryString = ''
    const utmParams = UTMParams.get()
    Object.keys(utmParams).forEach((k) => (queryString += `&${k}=${utmParams[k]}`))
    const baseUrl = cart.checkoutUrl
    setGettingCheckout(false)
    safeTrackerParam((param) => {
      if (param) queryString += `&${param}`
      window.location = baseUrl + queryString
    })
  }

  const handleApplyDiscount = async (discount) => {
    if (!discount) return
    await applyDiscount(discount)
  }

  const discountsArray = [{ applicableItemHandles: [] }]

  const bannerInfo = LocalStorage.getItem('banner')

  useEffect(() => {
    if (cart?.lines?.edges?.length) {
      if (shouldRemoveReturnGuarantee(cart?.lines?.edges)) {
        removeLineItem(
          cart?.lines?.edges.find(
            (item) => item.node.merchandise.product.handle === RETURN_GUARANTEE_PRODUCT_HANDLE
          )?.node?.id
        )
      }
      if (shouldRemoveBetterHelpProduct(cart?.lines?.edges)) {
        removeLineItem(
          cart?.lines?.edges.find(
            (item) => item.node.merchandise.product.handle === LOVEVERY_PRODUCT_HANDLE
          )?.node?.id
        )
      }
    }
    if (!checkShowPopup && cart?.lines?.edges?.length && hasFrame2 && !hasSnapFrame) {
      const timer = setTimeout(() => {
        setDisplayPopup(true)
        setcheckShowPopup(true)
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [cart, hasFrame2, hasSnapFrame])

  if (loading) {
    return (
      <CenteredContent>
        <img src={loadingGif} alt="Loading your cart..." />
      </CenteredContent>
    )
  }
  if (cart?.lines?.edges?.length === 0) {
    return (
      <Container>
        <Header>
          <Trans>You have no items in your cart. Check out our products!</Trans>
        </Header>
        <ProductListContainer>
          <ProductsList />
        </ProductListContainer>
      </Container>
    )
  }
  // const isDiscountApplied = Object.values(DISCOUNTS).includes(discountCode) && discountAmount > 0
  const items = cart?.lines?.edges.map((item) => (
    <LineItem
      key={item.node.id.toString()}
      item={item}
      discountsArray={discountsArray}
      updatingPrices={updatingPrices}
      setUpdatingPrices={setUpdatingPrices}
      // availableDiscount={availableDiscount}
      // setAvailableDiscount={setAvailableDiscount}
      // appliedBundleDiscount={appliedBundleDiscount}
      // setAppliedBundleDiscount={setAppliedBundleDiscount}
      bannerInfo={bannerInfo}
      strikethrough={false}
      subtotalQuantity={totalQuantity}
      setHasFrame2={setHasFrame2}
      setHasSnapFrame={setHasSnapFrame}
      setHasBetterHelp={setHasBetterHelp}
      setHasCalendar={setHasCalendar}
      isFreeProduct={item?.node?.merchandise?.product?.handle === RETURN_GUARANTEE_PRODUCT_HANDLE}
      isBetterHelpProduct={item?.node?.merchandise?.product?.handle === LOVEVERY_PRODUCT_HANDLE}
    />
  ))

  return (
    <>
      {displayPopup && (
        <Popup
          snapFrame={snapFrame}
          setDisplayPopup={setDisplayPopup}
          cartLines={cart?.lines}
          handleApplyDiscount={handleApplyDiscount}
        />
      )}
      {displayToolTip && (
        <ToolTip
          setDisplayToolTip={setDisplayToolTip}
          onClick={() => Analytics.track('Clicked Tooltip in Cart CTA')}
          amount={FormattedCurrency(displayedTotal / 4)}
        />
      )}
      <CheckoutContainer>
        {SHOW_BETTER_HELP_DESIGN && hasCalendar && (
          <BetterHelpOffer offerRedeemed={hasBetterHelp} setHasBetterHelp={setHasBetterHelp} />
        )}
        <Table>
          <thead>
            <tr>
              <TableHeader textAlign="left">
                <Trans>Product</Trans>
              </TableHeader>
              <DesktopOnlyTableHeader width="118px">
                <Trans>Price</Trans>
              </DesktopOnlyTableHeader>
              <QuantityHeader width="118px">
                <Trans>Quantity</Trans>
              </QuantityHeader>
              <DesktopOnlyTableHeader width="118px">
                <Trans>Subtotal</Trans>
              </DesktopOnlyTableHeader>
              <HiddenOnDesktop>
                <TableHeader width="60px">
                  <Trans>Price</Trans>
                </TableHeader>
              </HiddenOnDesktop>
            </tr>
          </thead>
          <tbody>{items}</tbody>
        </Table>
        <DisplayOnDesktop>
          <Table>
            <thead>
              <TableHeader textAlign="left" />
              {discountAmount > 0 ? (
                <>
                  <TableHeader width="118px">
                    <Trans>Subtotal</Trans>
                  </TableHeader>
                  <QuantityHeader width="118px">
                    <Trans>Discount</Trans>
                  </QuantityHeader>
                </>
              ) : (
                <>
                  <TableHeader />
                  <TableHeader />
                </>
              )}
              <DesktopOnlyTableHeader width="118px">
                <Trans>Grand Total</Trans>
              </DesktopOnlyTableHeader>
            </thead>
            <tbody>
              <TableRow>
                <TD />
                {discountAmount > 0 ? (
                  <>
                    <TD>
                      <LineItemTotal>
                        {updatingPrices ? (
                          <LoadingEllipsis />
                        ) : (
                          <s>{FormattedCurrency(displayedSubtotal)}</s>
                        )}
                      </LineItemTotal>
                    </TD>
                    <TD>
                      {discountAmount > 0 && (
                        <Discount>
                          <>
                            {updatingPrices ? (
                              <LoadingEllipsis />
                            ) : (
                              <Amount color="#2178af">{` ${FormattedCurrency(
                                discountAmount
                              )}`}</Amount>
                            )}
                          </>
                        </Discount>
                      )}
                    </TD>
                  </>
                ) : (
                  <>
                    <TD />
                    <TD />
                  </>
                )}
                <TD>
                  <Total>
                    {updatingPrices ? (
                      <Spinner />
                    ) : (
                      <Amount color="#f66951">{FormattedCurrency(displayedTotal)}</Amount>
                    )}
                  </Total>
                </TD>
              </TableRow>
            </tbody>
          </Table>
        </DisplayOnDesktop>
        <Checkout>
          <DisplayOnMobile>
            {discountAmount > 0 && (
              <>
                <LineItemTotal>
                  <span>
                    <Trans>Subtotal</Trans>
                  </span>
                  {updatingPrices ? (
                    <LoadingEllipsis />
                  ) : (
                    <Subtotal strikethrough={false}>
                      {FormattedCurrency(displayedSubtotal)}
                    </Subtotal>
                  )}
                </LineItemTotal>
                {discountAmount > 0 && (
                  <Discount>
                    <>
                      <span>
                        <Trans>Discount</Trans>{' '}
                      </span>
                      {updatingPrices ? (
                        <LoadingEllipsis />
                      ) : (
                        <DiscountText>{FormattedCurrency(discountAmount)}</DiscountText>
                      )}
                    </>
                  </Discount>
                )}
              </>
            )}
            <Total>
              <span>
                <Trans>Grand Total</Trans>
              </span>
              {updatingPrices ? <Spinner /> : <span>{FormattedCurrency(displayedTotal)}</span>}
            </Total>
          </DisplayOnMobile>
          {!IN_UK && (
            <Excluding>
              <Trans>Excluding Tax and Shipping</Trans>
            </Excluding>
          )}
          {!IS_FOREIGN_COUNTRY && buyingCalendar && !buyingPlus && (
            <>
              <Offer>
                Limited time offer: Financing from 0% APR* and 4 interest-free payments of{' '}
                {FormattedCurrency(displayedTotal / 4)} with
              </Offer>
              <ShopPay onTooltipClick={() => setDisplayToolTip(true)} isCart />
            </>
          )}
          {totalQuantity > 24 && (
            <Error>
              <Trans>For bulk orders (25+ Skylight products), please contact</Trans>{' '}
              <ALink href="mailto:help@myskylight.com"> help@myskylight.com </ALink>{' '}
              <Trans>or adjust your cart to under 25 items.</Trans>
            </Error>
          )}
          <CheckoutButtons marginTop={IN_UK}>
            {!buyingPlus && !IN_AU && (
              <PayPalCheckoutButton
                color="#ffc439"
                type="button"
                onClick={handleCheckout}
                disabled={cart?.lines?.edges?.length === 0 || totalQuantity > 24}
              >
                <GatsbyImage image={paypalLogo.childImageSharp.gatsbyImageData} alt="PayPal Logo" />
              </PayPalCheckoutButton>
            )}

            <CheckoutButton
              color={theme.coral}
              type="button"
              onClick={handleCheckout}
              disabled={cart?.lines?.edges?.length === 0 || totalQuantity > 24}
            >
              {gettingCheckout ? <Spinner color="white" size={26} /> : t('Check Out')}
            </CheckoutButton>
          </CheckoutButtons>
        </Checkout>
      </CheckoutContainer>{' '}
    </>
  )
}

export default Cart
